/**
 * UpdateBanner — discreet, non-blocking "Update available" surface.
 *
 * Mantlr 2026 pattern: a whisper, not a shout. Sits in the global app
 * shell (mount once at the route root), pops into view when
 * ``useAppUpdater`` flips to ``available``, and politely waits for the
 * user. NEVER auto-restarts the app mid-task — Granola / Linear playbook.
 *
 * States visualised:
 *   - ``available``         → "Aggiornamento disponibile · Aggiorna / Più tardi"
 *   - ``downloading``       → progress bar (bytes/total) + "Annulla" disabled
 *   - ``ready_to_restart``  → "Pronto · Riavvia ora"
 *   - ``error``             → red strip + retry
 *   - everything else       → render ``null``
 *
 * Keyboard a11y: dismiss with Esc, focus first action on mount,
 * ``role="status"`` + ``aria-live="polite"`` so screen readers surface
 * the announcement without interrupting the active flow.
 */

import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  CircleAlert,
  Loader2,
  RefreshCw,
  Sparkles,
  X,
} from "lucide-react";
import * as React from "react";

import { useAppUpdater } from "../hooks/useAppUpdater";

function formatMB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const UpdateBanner: React.FC = () => {
  const { i18n } = useLingui();
  const {
    state,
    update,
    downloaded,
    contentLength,
    error,
    check,
    installAndRestart,
    dismiss,
  } = useAppUpdater();

  // Esc dismisses ``available`` / ``error`` banners but never an
  // in-flight download (user already committed). Hook MUST run
  // unconditionally on every render — the early-return below would
  // otherwise change the hook count between renders and trip
  // React's Rules of Hooks. We gate the *listener body* on state
  // instead.
  React.useEffect(() => {
    if (state !== "available" && state !== "error") return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, dismiss]);

  // Hide entirely when there's no update to surface. Keeps the app
  // chrome silent 99% of the time — the banner is the exception, not
  // the default. All hooks above must be unconditional.
  if (
    state === "idle" ||
    state === "checking" ||
    state === "up_to_date" ||
    state === "unsupported"
  ) {
    return null;
  }

  const isError = state === "error";
  const progressPct =
    state === "downloading" && contentLength && contentLength > 0
      ? Math.min(100, Math.round((downloaded / contentLength) * 100))
      : null;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "pointer-events-auto fixed inset-x-0 bottom-3 z-50 mx-auto flex w-fit max-w-[min(680px,calc(100vw-32px))] items-center gap-3 rounded-lg border px-4 py-2.5 text-[13px] shadow-black/5 shadow-lg backdrop-blur",
        isError
          ? "border-error/40 bg-error/[0.08] text-error"
          : "border-hairline bg-card/95 text-ink-1",
      )}
    >
      {isError ? (
        <CircleAlert
          className="h-4 w-4 shrink-0 text-error"
          strokeWidth={2.25}
          aria-hidden
        />
      ) : (
        <Sparkles
          className="h-4 w-4 shrink-0 text-ando-orange"
          strokeWidth={2.25}
          aria-hidden
        />
      )}

      <div className="min-w-0 flex-1">
        {state === "available" ? (
          <>
            <p className="truncate font-medium">
              <Trans>Aggiornamento disponibile</Trans>
              {update ? (
                <span className="ml-1 font-normal text-ink-3">
                  · {update.version}
                </span>
              ) : null}
            </p>
            {update?.notes ? (
              <p className="truncate text-[11.5px] text-ink-3">
                {update.notes}
              </p>
            ) : null}
          </>
        ) : null}

        {state === "downloading" ? (
          <>
            <p className="truncate font-medium">
              <Trans>Sto scaricando l'aggiornamento…</Trans>
            </p>
            <div className="mt-1 flex items-center gap-2">
              <div
                className="h-1 flex-1 overflow-hidden rounded-full bg-ink-3/15"
                role="progressbar"
                aria-valuenow={progressPct ?? undefined}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full bg-ando-orange transition-[width] duration-200"
                  style={{
                    width:
                      progressPct !== null
                        ? `${progressPct}%`
                        : // Indeterminate: gentle pulse via min width.
                          "30%",
                  }}
                />
              </div>
              <span className="shrink-0 text-[10.5px] text-ink-3 tabular-nums">
                {progressPct !== null
                  ? `${progressPct}%`
                  : formatMB(downloaded)}
              </span>
            </div>
          </>
        ) : null}

        {state === "ready_to_restart" ? (
          <p className="truncate font-medium">
            <Trans>Aggiornamento pronto — riavvia per applicarlo</Trans>
          </p>
        ) : null}

        {state === "error" ? (
          <>
            <p className="truncate font-medium">
              <Trans>Errore aggiornamento</Trans>
            </p>
            <p className="truncate text-[11.5px]">{error}</p>
          </>
        ) : null}
      </div>

      {/* ── Actions ──────────────────────────────────────────────── */}
      {state === "available" ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => void installAndRestart()}
            className="rounded-md bg-ando-orange px-3 py-1 font-medium text-[12px] text-white hover:bg-ando-orange/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/70"
          >
            <Trans>Aggiorna</Trans>
          </button>
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md px-2 py-1 text-[12px] text-ink-3 hover:bg-black/[0.04]"
          >
            <Trans>Più tardi</Trans>
          </button>
        </div>
      ) : null}

      {state === "downloading" ? (
        <Loader2
          className="h-4 w-4 shrink-0 animate-spin text-ando-orange"
          strokeWidth={2.25}
          aria-hidden
        />
      ) : null}

      {state === "ready_to_restart" ? (
        <button
          type="button"
          onClick={() => void installAndRestart()}
          className="shrink-0 rounded-md bg-ando-orange px-3 py-1 font-medium text-[12px] text-white hover:bg-ando-orange/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/70"
        >
          <Trans>Riavvia ora</Trans>
        </button>
      ) : null}

      {state === "error" ? (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => void check()}
            aria-label={i18n._(msg`Riprova controllo aggiornamento`)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-error hover:bg-error/10"
          >
            <RefreshCw className="h-3 w-3" strokeWidth={2.25} aria-hidden />
            <Trans>Riprova</Trans>
          </button>
          <button
            type="button"
            onClick={dismiss}
            aria-label={i18n._(msg`Chiudi avviso`)}
            className="rounded-md px-1 py-1 text-error/70 hover:bg-error/10"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
          </button>
        </div>
      ) : null}
    </div>
  );
};
UpdateBanner.displayName = "UpdateBanner";
